import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, input, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { AIMessage, BaseMessage, HumanMessage, ToolMessage } from '@langchain/core/messages';
import { ChatMessageBubbleComponent } from '../chat-message-bubble/chat-message-bubble.component';
import { AiChatFacade } from '../../ai-chat.facade';
import {
  ChatSuggestionsComponent
} from "@components/mainmenu/reports-menu/ai-reports/ai-reports-sub-menu/chat/components/chat-suggestions/chat-suggestions.component";
import { INTERNAL_TOOL_NAMES, parseSubagentResult, SubagentResult } from 'src/app/common/models/ai-reports/shared/deep-agent-state';
import { AssetComponent } from '@components/mainmenu/reports-menu/ai-reports/ai-reports-sub-menu/asset/asset.component';
import { AssetMetaPipe } from 'src/app/pipes/AIReports/asset-meta.pipe';

/** True when a message has no user-facing text — the string is blank, or the content-block array is empty. */
function isEmptyContent(content: BaseMessage['content']): boolean {
  if (typeof content === 'string') return !content.trim();
  if (Array.isArray(content)) return content.length === 0;
  // compile-time exhaustiveness guard — fails if LangChain widens MessageContent.
  const exhaustive: never = content;
  return exhaustive;
}

// Avoids re-parsing the same ToolMessage's JSON payload on every streamed chunk.
const subagentResultCache = new WeakMap<ToolMessage, SubagentResult | null>();

function cachedParseSubagentResult(tm: ToolMessage): SubagentResult | null {
  if (!subagentResultCache.has(tm)) {
    subagentResultCache.set(tm, parseSubagentResult(tm.content));
  }
  return subagentResultCache.get(tm) ?? null;
}

export interface AssetTurnGroup {
  turn: number;
  assetIds: string[];
}

function assetTurnGroupsEqual(a: AssetTurnGroup[], b: AssetTurnGroup[]): boolean {
  return a.length === b.length && a.every((g, i) =>
    g.turn === b[i].turn && g.assetIds.length === b[i].assetIds.length && g.assetIds.every((id, j) => id === b[i].assetIds[j]));
}

@Component({
  selector: 'app-chat-message-list',
  standalone: true,
  imports: [CommonModule, TranslateModule, ChatMessageBubbleComponent, ChatSuggestionsComponent, AssetComponent, AssetMetaPipe],
  templateUrl: './chat-message-list.component.html',
  styleUrl: './chat-message-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatMessageListComponent {
  messages = input.required<BaseMessage[]>();
  isLoading = input<boolean>(false);
  isStreaming = input<boolean>(false);

  private readonly container = viewChild<ElementRef>('container');
  private readonly facade = inject(AiChatFacade);

  /** Forward a bubble's "Try again" click to the facade (the bubble stays presentational). */
  onRetry(): void {
    this.facade.retry();
  }

  readonly toolMessageMap = computed(() => {
    const map = new Map<string, ToolMessage>();
    for (const msg of this.messages()) {
      if (ToolMessage.isInstance(msg) && msg.tool_call_id) {
        map.set(msg.tool_call_id, msg);
      }
    }
    return map;
  });

  readonly visibleMessages = computed(() => {
    const internalCallIds = new Set<string>();
    for (const msg of this.messages()) {
      if (AIMessage.isInstance(msg)) {
        for (const tc of msg.tool_calls ?? []) {
          if (tc.id && INTERNAL_TOOL_NAMES.has(tc.name)) internalCallIds.add(tc.id);
        }
      }
    }
    return this.messages().filter(msg => {
      if (ToolMessage.isInstance(msg)) {
        return !(msg.tool_call_id && internalCallIds.has(msg.tool_call_id));
      }
      if (AIMessage.isInstance(msg)) {
        const toolCalls = msg.tool_calls ?? [];
        const allCallsInternal = toolCalls.length > 0 && toolCalls.every(tc => INTERNAL_TOOL_NAMES.has(tc.name));
        return !(allCallsInternal && isEmptyContent(msg.content));
      }
      return true;
    });
  });

  // Collects task-produced assets from ALL messages, grouped by request turn so a multi-turn session can attribute each asset.
  readonly assetsByTurn = computed<AssetTurnGroup[]>(() => {
    const groups: AssetTurnGroup[] = [];
    const seen = new Set<string>();
    const toolMessages = this.toolMessageMap();
    let turn = 0;
    let currentIds: string[] = [];
    for (const msg of this.messages()) {
      if (HumanMessage.isInstance(msg)) {
        if (currentIds.length) groups.push({turn, assetIds: currentIds});
        turn++;
        currentIds = [];
        continue;
      }
      if (!AIMessage.isInstance(msg)) continue;
      for (const tc of msg.tool_calls ?? []) {
        if (tc.name !== 'task' || !tc.id) continue;
        const tm = toolMessages.get(tc.id);
        if (!tm) continue;
        // Subagent LLMs occasionally emit image URLs instead of asset ids — drop them before any meta lookup.
        cachedParseSubagentResult(tm)?.assets?.forEach(id => {
          if (/^[\w-]{1,128}$/.test(id) && !seen.has(id)) {
            seen.add(id);
            currentIds.push(id);
          }
        });
      }
    }
    if (currentIds.length) groups.push({turn, assetIds: currentIds});
    return groups;
  }, {equal: assetTurnGroupsEqual});

  constructor() {
    effect(() => {
      // Trigger scroll when messages change or streaming status changes
      this.messages();
      this.isStreaming();

      // We need to wait for the DOM to update. Since we're in an effect,
      // we can use a small timeout or just scroll in the next microtask.
      setTimeout(() => this.scrollToBottom(), 0);
    });
  }

  private scrollToBottom(): void {
    const el = this.container()?.nativeElement;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }
}
